import { create } from 'zustand';

/**
 * Zustand store for managing upload state
 * Replaces the Redux-based ingest-upload store
 */
const useUploadStore = create((set) => ({
	// Map of uploadId -> upload info (for file ingests)
	uploads: {},

	// Map of uploadId -> upload info (for graphics projects)
	graphicsProjects: {},

	// Register a new file upload
	addFileUpload: (uploadId, name) => set((state) => ({
		uploads: {
			...state.uploads,
			[uploadId]: {
				name,
				progress: 0,
				error: null,
				retryInfo: null
			}
		}
	})),

	// Update file upload progress
	updateFileUpload: (uploadId, progress, retryInfo = null) => set((state) => ({
		uploads: {
			...state.uploads,
			[uploadId]: {
				...state.uploads[uploadId],
				progress,
				retryInfo
			}
		}
	})),

	// Mark file upload as failed
	failFileUpload: (uploadId, error) => set((state) => ({
		uploads: {
			...state.uploads,
			[uploadId]: {
				...state.uploads[uploadId],
				progress: 0,
				error
			}
		}
	})),

	// Mark file upload as successful
	successFileUpload: (uploadId) => set((state) => {
		const existingUpload = state.uploads[uploadId];
		if(!existingUpload) {
			// No-op if the upload has not been registered yet
			return state;
		}

		return {
			uploads: {
				...state.uploads,
				[uploadId]: {
					...existingUpload,
					progress: 1
				}
			}
		};
	}),
	// Register a new graphics project upload
	addGraphicsProjectUpload: (uploadId, name) => set((state) => ({
		graphicsProjects: {
			...state.graphicsProjects,
			[uploadId]: {
				name,
				progress: 0,
				error: null
			}
		}
	})),

	// Update graphics project upload progress
	updateGraphicsProjectUpload: (uploadId, progress) => set((state) => ({
		graphicsProjects: {
			...state.graphicsProjects,
			[uploadId]: {
				...state.graphicsProjects[uploadId],
				progress
			}
		}
	})),

	// Mark graphics project upload as failed
	failGraphicsProjectUpload: (uploadId, error) => set((state) => ({
		graphicsProjects: {
			...state.graphicsProjects,
			[uploadId]: {
				...state.graphicsProjects[uploadId],
				progress: 0,
				error
			}
		}
	})),

	// Mark graphics project upload as successful
	successGraphicsProjectUpload: (uploadId) => set((state) => ({
		graphicsProjects: {
			...state.graphicsProjects,
			[uploadId]: {
				...state.graphicsProjects[uploadId],
				progress: 1
			}
		}
	})),

	// Clear all completed uploads
	clearCompletedUploads: () => set((state) => {
		const uploads = {};
		Object.keys(state.uploads).forEach((id) => {
			if(state.uploads[id].progress !== 1) {
				uploads[id] = state.uploads[id];
			}
		});
		return { uploads };
	}),

	// Clear all completed graphics project uploads
	clearCompletedGraphicsProjects: () => set((state) => {
		const graphicsProjects = {};
		Object.keys(state.graphicsProjects).forEach((id) => {
			if(state.graphicsProjects[id].progress !== 1) {
				graphicsProjects[id] = state.graphicsProjects[id];
			}
		});
		return { graphicsProjects };
	})
}));

// Selectors - use these to get data from the store
export const selectFileUploads = (state) => {
	return Object.keys(state.uploads).map((id) => ({
		id,
		...state.uploads[id]
	}));
};

export const selectGraphicsProjectUploads = (state) => {
	return Object.keys(state.graphicsProjects).map((id) => ({
		id,
		...state.graphicsProjects[id]
	}));
};

export const selectIsUploadingGraphicsProject = (state) => {
	return Object.values(state.graphicsProjects).some(
		(upload) => upload.progress > 0 && upload.progress < 1
	);
};

// Selector to get upload progress (0-1)
export const selectUploadProgress = (state) => {
	const uploads = Object.values(state.uploads);
	if(uploads.length === 0) {
		return 0;
	}
	return uploads.reduce((sum, upload) => sum + upload.progress, 0) / uploads.length;
};

// Selector to check if there are any running uploads (progress < 1)
export const selectHasRunningUploads = (state) => {
	return Object.values(state.uploads).some((upload) => upload.progress < 1);
};

export default useUploadStore;

